import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Landmark, Search, Plus, FileText, Download, Eye, AlertTriangle, Pencil, History, StickyNote, X, RotateCcw, Trash2, Mail } from 'lucide-react';
import { Textarea } from './ui/textarea';
import CemeteryFormDialog from './CemeteryFormDialog';
import ApplicationDetailsDialog from './ApplicationDetailsDialog';

const STATUS_META = {
  not_required:       { label: 'Not Required',       cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  application_required:{ label: 'Application Required', cls: 'bg-orange-100 text-orange-800 border-orange-300' },
  draft:              { label: 'Draft',              cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  ready_to_submit:    { label: 'Ready to Submit',    cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  submitted:          { label: 'Submitted',          cls: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  awaiting_approval:  { label: 'Awaiting Approval',  cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  changes_requested:  { label: 'Changes Requested',  cls: 'bg-red-100 text-red-700 border-red-300' },
  approved:           { label: 'Approved',           cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  rejected:           { label: 'Rejected',           cls: 'bg-red-100 text-red-800 border-red-300' },
};

const fmtBool = (v) => (v === true ? 'Yes' : v === false ? 'No' : null);
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return d; } };

function ReqLine({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between gap-2 text-sm py-0.5">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800 text-right">{value}</span>
    </div>
  );
}

export default function MemorialApplicationSection({ orderId, order, onOrderUpdate }) {
  const { token, API } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [changingCemetery, setChangingCemetery] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizeAs, setFinalizeAs] = useState('submitted');
  const [recipient, setRecipient] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  const [sendNow, setSendNow] = useState(true);
  const [sendingSign, setSendingSign] = useState(false);
  const [signEmail, setSignEmail] = useState('');
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [canEditCemetery, setCanEditCemetery] = useState(false);
  const [emailVersion, setEmailVersion] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/application`, auth);
      setState(res.data);
      setCanEditCemetery(!!res.data.can_edit);
    } catch { setState(null); }
    finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const doSearch = (q) => {
    setQuery(q);
    clearTimeout(searchTimer.current);
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/cemeteries/search`, { ...auth, params: { q } });
        setResults(res.data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
  };

  const selectCemetery = async (cem) => {
    try {
      if (state?.application) {
        await axios.put(`${API}/orders/${orderId}/application`, { cemetery_id: cem.id }, auth);
        toast.success(`Cemetery changed to ${cem.name}`);
      } else {
        await axios.post(`${API}/orders/${orderId}/application`, { cemetery_id: cem.id }, auth);
        toast.success(`Application started for ${cem.name}`);
      }
      setQuery(''); setResults([]); setChangingCemetery(false);
      load(); onOrderUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link cemetery');
    }
  };

  const changeStatus = async (status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/application`, { status }, auth);
      toast.success(`Application status: ${STATUS_META[status]?.label || status}`);
      load(); onOrderUpdate?.();
    } catch { toast.error('Failed to update status'); }
  };

  const changeTemplate = async (template_id) => {
    try {
      await axios.put(`${API}/orders/${orderId}/application`, { template_id: template_id === 'standard' ? '' : template_id }, auth);
      toast.success(template_id === 'standard' ? 'Using the Memorial Desk standard form' : 'Application form template selected');
      load(); onOrderUpdate?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to select template'); }
  };

  const sendForSignature = async () => {
    setSendingSign(true);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/application/send-for-signature`,
        { to_email: signEmail || undefined, origin: window.location.origin }, auth);
      if (res.data.sent) toast.success(`Application sent to ${res.data.to_email} to sign`);
      else toast.warning(`Signing link created but email could not be sent${res.data.error ? ': ' + res.data.error : ''}. Share the link manually.`);
      setSignDialogOpen(false);
      load(); onOrderUpdate?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send for signature'); }
    finally { setSendingSign(false); }
  };

  const previewPdf = async () => {
    try {
      const res = await axios.post(`${API}/orders/${orderId}/application/generate-pdf`, {},
        { ...auth, responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch { toast.error('Failed to generate application PDF'); }
  };

  const PROGRESS_STEPS = [
    'Collecting order & memorial details…',
    'Fetching cemetery requirements…',
    'Embedding artwork & dimensions…',
    'Building the application PDF…',
    'Emailing the cemetery…',
  ];

  const finalize = async () => {
    setFinalizing(true);
    setProgressStep(0);
    const maxStep = sendNow ? PROGRESS_STEPS.length - 1 : PROGRESS_STEPS.length - 2;
    const timer = setInterval(() => {
      setProgressStep((s) => (s < maxStep ? s + 1 : s));
    }, 2200);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/application/finalize`,
        { mark_as: finalizeAs, recipient_email: recipient || null, send_email: sendNow }, auth);
      const emailInfo = res.data?.email;
      if (sendNow && emailInfo) {
        if (emailInfo.sent) {
          toast.success(emailInfo.demo_mode
            ? 'Snapshot saved — demo mode, email logged but not actually sent'
            : `Application created and emailed to ${emailInfo.to}`);
        } else {
          toast.warning(`Snapshot saved, but the email did not send: ${emailInfo.error}. You can send it manually from the version list.`);
        }
      } else {
        toast.success(finalizeAs === 'submitted'
          ? 'Application submitted — snapshot saved'
          : 'Snapshot saved — ready to submit');
      }
      setFinalizeOpen(false);
      load(); onOrderUpdate?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to finalize application'); }
    finally { clearInterval(timer); setFinalizing(false); setProgressStep(0); }
  };

  const resetDraft = async () => {
    if (!window.confirm('Clear all manually entered application details (deceased, deed holder, dimensions, notes) and reset the draft to defaults? Order data is unaffected.')) return;
    try {
      await axios.post(`${API}/orders/${orderId}/application/reset`, {}, auth);
      toast.success('Application draft reset to defaults');
      load(); onOrderUpdate?.();
    } catch { toast.error('Failed to reset draft'); }
  };

  const removeApplication = async () => {
    if (!window.confirm('Remove this application from the order completely?')) return;
    try {
      await axios.delete(`${API}/orders/${orderId}/application`, auth);
      toast.success('Application removed');
      load(); onOrderUpdate?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove application'); }
  };

  const openEmailDialog = (v) => {
    const cem = state?.cemetery;
    const req2 = cem?.requirements || {};
    setEmailTo(v.recipient_email || req2.submission_email || cem?.application_email || '');
    setEmailSubject(`Memorial application — ${cem?.name || 'cemetery'}${order?.order_number ? ` — Order ${order.order_number}` : ''}`);
    setEmailMessage(
`Dear Sir/Madam,

Please find attached our memorial permit application${state?.draft_data?.plot_number ? ` for grave/plot ${state.draft_data.plot_number}` : ''} at ${cem?.name || 'your cemetery'}.

We would be grateful if you could confirm receipt and advise us of the outcome in due course.

Kind regards,
${state?.draft_data?.mason_company_name || ''}`);
    setEmailVersion(v);
  };

  const sendEmail = async () => {
    if (!emailTo.trim() || !emailTo.includes('@')) { toast.error('Please enter a valid recipient email'); return; }
    setSendingEmail(true);
    try {
      const res = await axios.post(
        `${API}/orders/${orderId}/application/versions/${emailVersion.id}/email`,
        { to: emailTo.trim(), subject: emailSubject, message: emailMessage.replace(/\n/g, '<br/>') }, auth);
      toast.success(res.data.demo_mode
        ? 'Demo mode — email logged but not actually sent'
        : `Application emailed to ${emailTo.trim()}`);
      setEmailVersion(null);
      load(); onOrderUpdate?.();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed to send email');
    } finally { setSendingEmail(false); }
  };

  const downloadVersionPdf = async (vid, version) => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/application/versions/${vid}/pdf`,
        { ...auth, responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch { toast.error(`Could not open version ${version} PDF`); }
  };

  const openDoc = async (docId) => {
    try {
      const res = await axios.get(`${API}/cemeteries/${state.cemetery.id}/documents/${docId}/download`,
        { ...auth, responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch { toast.error('Could not open document'); }
  };

  if (order?.order_type === 'grave vault') return null;
  if (loading || !state) return null;

  const { application, cemetery, documents = [], versions = [], warnings = [], draft_data: draft, applicable_fee: fee } = state;
  const availableTemplates = state.available_templates || [];
  const selectedTemplate = state.selected_template || null;
  const suggestedTemplateId = state.suggested_template_id || null;
  const templateMissing = state.template_missing_fields || [];
  const suggestedTemplate = availableTemplates.find((t) => t.id === suggestedTemplateId);
  const signature = state.signature || { status: 'not_requested' };
  const req = cemetery?.requirements || {};
  const statusMeta = STATUS_META[application?.status] || STATUS_META.draft;
  const submissionEmail = req.submission_email || cemetery?.application_email;
  const appForm = documents.find((d) => d.kind === 'application_form');
  const regulations = documents.find((d) => d.kind === 'regulations');
  const showSearch = !application || changingCemetery;

  return (
    <Card className="border-l-4 border-l-teal-600" data-testid="memorial-application-section">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Landmark className="w-5 h-5 text-teal-600" />
            Memorial Application
          </CardTitle>
          {application && (
            <div className="flex items-center gap-2">
              <Badge className={`border ${statusMeta.cls}`} data-testid="application-status-badge">{statusMeta.label}</Badge>
              <Select value={application.status} onValueChange={changeStatus}>
                <SelectTrigger className="h-8 w-44" data-testid="application-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(state.statuses || Object.keys(STATUS_META)).map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_META[s]?.label || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showSearch && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <Input
                  className="pl-9"
                  placeholder="Search cemetery / churchyard by name, postcode or authority…"
                  value={query}
                  onChange={(e) => doSearch(e.target.value)}
                  data-testid="cemetery-search-input"
                />
              </div>
              <Button variant="outline" onClick={() => setAddOpen(true)} data-testid="add-cemetery-btn">
                <Plus className="w-4 h-4 mr-1" /> Add New Cemetery / Churchyard
              </Button>
              {changingCemetery && (
                <Button variant="ghost" size="icon" onClick={() => { setChangingCemetery(false); setQuery(''); setResults([]); }}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            {searching && <p className="text-xs text-slate-400">Searching…</p>}
            {results.length > 0 && (
              <div className="border rounded-md divide-y max-h-64 overflow-y-auto" data-testid="cemetery-search-results">
                {results.map((c) => (
                  <button key={c.id} onClick={() => selectCemetery(c)}
                    className="w-full text-left px-3 py-2 hover:bg-teal-50 transition-colors"
                    data-testid={`cemetery-result-${c.id}`}>
                    <p className="text-sm font-medium">{c.name}</p>
                    <p className="text-xs text-slate-500">
                      {[c.burial_authority, c.address, c.postcode].filter(Boolean).join(' · ')}
                      {c.source === 'burial_authority_verified' && <span className="text-emerald-600 ml-1">✓ verified</span>}
                    </p>
                  </button>
                ))}
              </div>
            )}
            {!searching && query.trim() && results.length === 0 && (
              <p className="text-sm text-slate-500">No matches found — use "Add New Cemetery / Churchyard" to create it.</p>
            )}
          </div>
        )}

        {application && cemetery && !changingCemetery && (
          <>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-slate-800">{cemetery.name}</p>
                <p className="text-sm text-slate-500">
                  {[cemetery.burial_authority, cemetery.address, cemetery.postcode].filter(Boolean).join(' · ')}
                </p>
                <div className="flex gap-1 mt-1 flex-wrap">
                  <Badge variant="outline" className="text-xs">{(cemetery.source || '').replace(/_/g, ' ')}</Badge>
                  {draft?.memorial_category_label && (
                    <Badge className="text-xs bg-teal-100 text-teal-800 border border-teal-300" data-testid="application-memorial-category-badge">
                      {draft.memorial_category_label}
                    </Badge>
                  )}
                  {cemetery.last_verified_date && (
                    <Badge variant="outline" className="text-xs">Verified {fmtDate(cemetery.last_verified_date)}</Badge>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-cemetery-btn">
                  <Pencil className="w-3.5 h-3.5 mr-1" /> {canEditCemetery ? 'Edit Info' : 'Suggest Update'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setChangingCemetery(true)} data-testid="change-cemetery-btn">
                  Change
                </Button>
              </div>
            </div>

            {warnings.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-1" data-testid="application-warnings">
                {warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-800 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {w}
                  </p>
                ))}
              </div>
            )}

            <div className="rounded-md border bg-slate-50 p-3" data-testid="application-requirements">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Application Requirements</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <ReqLine label={`Application fee (${order?.order_type === 'add on inscription' ? 'add-on inscription' : 'this order'})`} value={fee != null ? `£${Number(fee).toFixed(2)}` : null} />
                <ReqLine label="Fee — new memorial" value={cemetery.application_fee != null ? `£${Number(cemetery.application_fee).toFixed(2)}` : null} />
                <ReqLine label="Fee — add-on inscription" value={cemetery.application_fee_inscription != null ? `£${Number(cemetery.application_fee_inscription).toFixed(2)}` : null} />
                <ReqLine label="Own form required" value={fmtBool(req.own_form_required)} />
                <ReqLine label="Memorial Desk application accepted" value={req.memorial_desk_application_accepted ? req.memorial_desk_application_accepted.charAt(0).toUpperCase() + req.memorial_desk_application_accepted.slice(1) : null} />
                <ReqLine label="Deed holder signature" value={fmtBool(req.deed_holder_signature_required)} />
                <ReqLine label="Applicant signature" value={fmtBool(req.applicant_signature_required)} />
                <ReqLine label="Mason signature" value={fmtBool(req.mason_signature_required)} />
                <ReqLine label="Drawing required" value={fmtBool(req.drawing_required)} />
                <ReqLine label="Photograph required" value={fmtBool(req.photograph_required)} />
                <ReqLine label="Grave/plot number required" value={fmtBool(req.grave_number_required)} />
                <ReqLine label="Full inscription required" value={fmtBool(req.full_inscription_required)} />
                <ReqLine label="Fixing method required" value={fmtBool(req.fixing_method_required)} />
                <ReqLine label="BRAMM required" value={fmtBool(req.bramm_required)} />
                <ReqLine label="NAMM required" value={fmtBool(req.namm_required)} />
                <ReqLine label="BS 8415 compliance" value={fmtBool(req.bs8415_required)} />
                {(() => {
                  const cat = draft?.memorial_category || 'full';
                  const pick = (base) => cat === 'cremation' ? (req[`cremation_${base}`] || req[base])
                    : cat === 'children' ? (req[`children_${base}`] || req[base])
                    : req[base];
                  const suffix = cat === 'cremation' ? ' (cremation)' : cat === 'children' ? " (children's)" : '';
                  if (cat === 'add_on') return null;
                  return (
                    <>
                      <ReqLine label={`Max height${suffix}`} value={pick('max_height')} />
                      <ReqLine label={`Max width${suffix}`} value={pick('max_width')} />
                      <ReqLine label={`Max thickness${suffix}`} value={pick('max_thickness')} />
                    </>
                  );
                })()}
                <ReqLine label="Kerb sets permitted" value={fmtBool(req.kerb_sets_permitted)} />
                <ReqLine label="Cover slabs permitted" value={fmtBool(req.cover_slabs_permitted)} />
                <ReqLine label="Application email" value={submissionEmail} />
              </div>
              {(cemetery.custom_requirements || []).map((c, i) => (
                <ReqLine key={i} label={c.label} value={c.value} />
              ))}
              {req.additional_requirements && (
                <p className="text-xs text-slate-600 mt-2 whitespace-pre-line">{req.additional_requirements}</p>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                {appForm && (
                  <Button variant="outline" size="sm" onClick={() => openDoc(appForm.id)} data-testid="view-authority-form-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> Authority Form
                  </Button>
                )}
                {regulations && (
                  <Button variant="outline" size="sm" onClick={() => openDoc(regulations.id)} data-testid="view-regulations-btn">
                    <FileText className="w-3.5 h-3.5 mr-1" /> Regulations
                  </Button>
                )}
              </div>
            </div>

            <div className="rounded-md border border-teal-200 bg-teal-50/60 p-3 space-y-2" data-testid="application-form-template">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Application form</p>
                  <p className="text-xs text-slate-500">Generate on an official authority form instead of the standard Memorial Desk form.</p>
                </div>
                <Select value={selectedTemplate?.id || 'standard'} onValueChange={changeTemplate}>
                  <SelectTrigger className="h-9 w-72" data-testid="application-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Memorial Desk standard form</SelectItem>
                    {availableTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}{t.id === suggestedTemplateId ? ' ⭐' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {suggestedTemplate && (!selectedTemplate || selectedTemplate.id !== suggestedTemplateId) && (
                <button className="text-xs text-teal-700 underline" onClick={() => changeTemplate(suggestedTemplateId)} data-testid="use-suggested-template">
                  Suggested for this {cemetery?.diocese ? 'diocese' : 'authority'}: use “{suggestedTemplate.name}”
                </button>
              )}
              {selectedTemplate && templateMissing.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="template-missing-fields">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  {templateMissing.length} required field{templateMissing.length > 1 ? 's' : ''} still needed for this form
                  ({templateMissing.slice(0, 4).map((m) => m.label).join(', ')}{templateMissing.length > 4 ? '…' : ''}) —
                  <button className="underline font-medium" onClick={() => setDetailsOpen(true)} data-testid="fill-missing-fields-btn">complete them</button>
                </div>
              )}
              {selectedTemplate && templateMissing.length === 0 && (
                <p className="text-xs text-emerald-700 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> All required fields for “{selectedTemplate.name}” are filled from the order.</p>
              )}
            </div>

            <div className="rounded-md border border-indigo-200 bg-indigo-50/60 p-3 space-y-2" data-testid="application-signature-panel">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" /> Customer signature
                  </p>
                  <p className="text-xs text-slate-500">Have the customer e-sign the application before it's sent to the church / cemetery.</p>
                </div>
                {signature.status === 'signed' ? (
                  <Badge className="text-xs bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid="signature-status-signed">
                    ✓ Signed{signature.signed_by ? ` by ${signature.signed_by}` : ''}{signature.signed_at ? ` · ${fmtDate(signature.signed_at)}` : ''}
                  </Badge>
                ) : signature.status === 'awaiting_signature' ? (
                  <Badge className="text-xs bg-amber-100 text-amber-800 border border-amber-300" data-testid="signature-status-awaiting">Awaiting customer signature</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs" data-testid="signature-status-none">Not requested</Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm"
                  onClick={() => { setSignEmail(order?.client_email || ''); setSignDialogOpen(true); }}
                  data-testid="send-for-signature-btn">
                  <Mail className="w-3.5 h-3.5 mr-1" />
                  {signature.status === 'awaiting_signature' ? 'Resend to Customer'
                    : signature.status === 'signed' ? 'Send Again' : 'Send to Customer to Sign'}
                </Button>
              </div>
              {signature.status !== 'signed' && (
                <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Not yet signed by the customer — sign before submitting to the church / cemetery.</p>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setDetailsOpen(true)} data-testid="application-details-btn">
                <StickyNote className="w-4 h-4 mr-1.5" /> Application Details
              </Button>
              <Button variant="outline" onClick={previewPdf} data-testid="preview-application-pdf-btn">
                <Eye className="w-4 h-4 mr-1.5" /> Preview Draft PDF
              </Button>
              <Button className="bg-teal-600 hover:bg-teal-700"
                onClick={() => { setRecipient(submissionEmail || ''); setFinalizeOpen(true); }}
                data-testid="finalize-application-btn">
                <FileText className="w-4 h-4 mr-1.5" /> Generate & Finalize Application
              </Button>
              <Button variant="ghost" size="sm" className="text-slate-500" onClick={resetDraft} data-testid="reset-application-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset Draft
              </Button>
              {versions.length === 0 && (
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={removeApplication} data-testid="remove-application-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                </Button>
              )}
            </div>

            {versions.length > 0 && (
              <div data-testid="application-history">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                  <History className="w-3.5 h-3.5" /> Application History
                </p>
                <div className="space-y-1.5">
                  {versions.map((v) => (
                    <div key={v.id} className="flex items-center justify-between border rounded-md px-3 py-1.5">
                      <div className="text-sm">
                        <span className="font-medium">Version {v.version}</span>
                        <span className="text-slate-500"> — {v.status_at_snapshot === 'submitted' ? 'Submitted' : 'Ready to submit'} {fmtDate(v.submitted_at)} by {v.submitted_by}</span>
                        {v.recipient_email && <span className="text-xs text-slate-400 block">To: {v.recipient_email}</span>}
                        {(v.email_log || []).map((e, i) => (
                          <span key={i} className="text-xs text-emerald-600 block">✓ Emailed to {e.to} on {fmtDate(e.sent_at)}</span>
                        ))}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEmailDialog(v)} data-testid={`version-email-${v.version}`}>
                          <Mail className="w-3.5 h-3.5 mr-1" /> Email
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => downloadVersionPdf(v.id, v.version)} data-testid={`version-pdf-${v.version}`}>
                          <Download className="w-3.5 h-3.5 mr-1" /> PDF
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {!application && !query.trim() && (
          <p className="text-xs text-slate-400">
            Search the shared Memorial Desk cemetery database to start a memorial permit application for this order.
          </p>
        )}
      </CardContent>

      <CemeteryFormDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        cemetery={null}
        onSaved={(cem) => selectCemetery(cem)}
      />
      <CemeteryFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        cemetery={cemetery}
        canEdit={canEditCemetery}
        onSaved={() => load()}
      />
      <Dialog open={signDialogOpen} onOpenChange={setSignDialogOpen}>
        <DialogContent className="max-w-md" data-testid="send-signature-dialog">
          <DialogHeader>
            <DialogTitle>Send application to customer to sign</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              We'll email the customer a secure one-time link to review and e-sign this application. Once signed, the signature is applied to the form and you can submit it to the church / cemetery.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Customer email</Label>
            <Input value={signEmail} onChange={(e) => setSignEmail(e.target.value)} placeholder="customer@example.com" data-testid="signature-email-input" />
            {selectedTemplate && templateMissing.length > 0 && (
              <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {templateMissing.length} required field(s) are still empty — consider completing them first.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignDialogOpen(false)} disabled={sendingSign}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={sendForSignature} disabled={sendingSign} data-testid="confirm-send-signature-btn">
              {sendingSign ? 'Sending…' : 'Send for Signature'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ApplicationDetailsDialog
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        orderId={orderId}
        application={application}
        draftData={draft}
        templateMissing={templateMissing}
        selectedTemplate={selectedTemplate}
        onSaved={() => load()}
      />

      <Dialog open={!!emailVersion} onOpenChange={(o) => !o && setEmailVersion(null)}>
        <DialogContent className="max-w-lg" data-testid="email-application-dialog">
          <DialogHeader>
            <DialogTitle>Email Application (Version {emailVersion?.version})</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Sends the finalized application PDF to the cemetery's application address using your company email settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">To</Label>
              <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="applications@council.gov.uk" data-testid="email-to-input" />
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} data-testid="email-subject-input" />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <Textarea rows={7} value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} data-testid="email-message-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailVersion(null)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={sendingEmail} className="bg-teal-600 hover:bg-teal-700" data-testid="email-send-btn">
              <Mail className="w-4 h-4 mr-1.5" /> {sendingEmail ? 'Sending…' : 'Send Application'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <DialogContent className="max-w-md" data-testid="finalize-dialog">
          <DialogHeader>
            <DialogTitle>Finalize Application</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              This creates an immutable snapshot (version {(application?.current_version || 0) + 1}) of the application
              with the current order data and a generated PDF. Later order changes will NOT alter this version.
            </DialogDescription>
          </DialogHeader>
          {finalizing ? (
            <div className="py-6 text-center space-y-4" data-testid="finalize-progress">
              <div className="w-10 h-10 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin mx-auto" />
              <p className="text-sm font-medium text-slate-800">{PROGRESS_STEPS[Math.min(progressStep, sendNow ? 4 : 3)]}</p>
              <p className="text-xs text-slate-500">This gathers a lot of order data and builds the full form — it can take up to a minute.</p>
              <div className="flex justify-center gap-1.5">
                {PROGRESS_STEPS.slice(0, sendNow ? 5 : 4).map((_, i) => (
                  <span key={i} className={`w-2 h-2 rounded-full ${i <= progressStep ? 'bg-teal-600' : 'bg-slate-200'}`} />
                ))}
              </div>
            </div>
          ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Mark as</Label>
              <Select value={finalizeAs} onValueChange={setFinalizeAs}>
                <SelectTrigger data-testid="finalize-mark-as"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="submitted">Submitted to authority</SelectItem>
                  <SelectItem value="ready_to_submit">Ready to submit (not sent yet)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Cemetery / church application email</Label>
              <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="applications@council.gov.uk" data-testid="finalize-recipient" />
            </div>
            <label className="flex items-start gap-2 p-3 rounded-md border border-teal-200 bg-teal-50 cursor-pointer">
              <input
                type="checkbox"
                checked={sendNow}
                onChange={(e) => setSendNow(e.target.checked)}
                className="mt-0.5"
                data-testid="finalize-send-now"
              />
              <span className="text-xs text-slate-700">
                <strong>Email it to the cemetery/church now</strong><br />
                Sends the application PDF automatically after creating it{recipient ? ` (to ${recipient})` : ' — uses the email above or the one on file'}.
              </span>
            </label>
          </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinalizeOpen(false)} disabled={finalizing}>Cancel</Button>
            <Button onClick={finalize} disabled={finalizing} className="bg-teal-600 hover:bg-teal-700" data-testid="finalize-confirm-btn">
              {finalizing ? 'Working…' : (sendNow ? 'Create & Send to Cemetery' : 'Create Snapshot & PDF')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
